type SearchPrefillQuery = {
  origin?: string
  destination?: string
  outboundDate?: string
  inboundDate?: string
  hasPrefillValues: boolean
  errors: string[]
}

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const normalizeText = (value: string | null): string | undefined => {
  if (!value) return undefined
  const trimmed = value.trim()
  return trimmed.length ? trimmed : undefined
}

const isValidIsoDate = (value: string): boolean => {
  if (!DATE_PATTERN.test(value)) return false

  const [year, month, day] = value.split('-').map(Number)
  const utcDate = new Date(Date.UTC(year, month - 1, day))

  return (
    utcDate.getUTCFullYear() === year &&
    utcDate.getUTCMonth() === month - 1 &&
    utcDate.getUTCDate() === day
  )
}

export const parseSearchPrefillQuery = (search: string): SearchPrefillQuery => {
  const queryParams = new URLSearchParams(search)
  const errors: string[] = []

  const origin = normalizeText(queryParams.get('origin'))
  const destination = normalizeText(queryParams.get('destination'))
  const outboundDateRaw = normalizeText(queryParams.get('outboundDate'))
  const inboundDateRaw =
    normalizeText(queryParams.get('inboundDate')) ?? normalizeText(queryParams.get('returnDate'))

  let outboundDate: string | undefined
  if (outboundDateRaw) {
    if (isValidIsoDate(outboundDateRaw)) {
      outboundDate = outboundDateRaw
    } else {
      errors.push('Invalid outboundDate format in query string')
    }
  }

  let inboundDate: string | undefined
  if (inboundDateRaw) {
    if (isValidIsoDate(inboundDateRaw)) {
      inboundDate = inboundDateRaw
    } else {
      errors.push('Invalid inboundDate format in query string')
    }
  }

  if (outboundDate && inboundDate && inboundDate < outboundDate) {
    errors.push('Inbound date is before outbound date in query string')
    inboundDate = undefined
  }

  const hasPrefillValues = !!(origin || destination || outboundDate || inboundDate)

  return {
    origin,
    destination,
    outboundDate,
    inboundDate,
    hasPrefillValues,
    errors,
  }
}

export type { SearchPrefillQuery }
